import React, { useState } from "react";
import {
  GeolocateControl,
  FullscreenControl,
  NavigationControl,
  Map,
  Source,
  Layer,
  Popup
} from "react-map-gl";
import { CenteredPinLayer, unclusteredPointLayer } from "./layers";
import "mapbox-gl/dist/mapbox-gl.css";
import styles from "./index.module.scss";
import {
  dateMonthFormat,
  formatAbbreviatedCurrency,
} from "../../../utils/utils";
import { useSearchParams } from "react-router-dom";
import { useFlipperVaultPRE } from "../../../hooks/react-query/inv-exchange/useFlipperVaultPRE";
const TOKEN = process.env.REACT_APP_MAP_KEY;

const parseRecordingDate = (dateVal: string | number | null | undefined) => {
  if (dateVal === null || dateVal === undefined || dateVal === "") return null;
  const dateStr = String(dateVal);
  if (!/^\d{8}$/.test(dateStr)) return null;
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return dateMonthFormat(`${year}-${month}-${day}`);
};

export default function MapSection({
  visibleCardId,
  type,
  acctId
}: {
  acctId?: string;
  type?: any;
  visibleCardId: string | null;
}) {
  const { data: flipperVaultPRE } = useFlipperVaultPRE();
  const mapRef: any = React.useRef<any>(null);
  const [styleLoaded, setStyleLoaded] = React.useState(false);
  const [searchParams] = useSearchParams();

  const expandedId = String(searchParams.get("expandedId"));

  const [initialCoords] = useState(() => {
    return {
      latitude: acctId
        ? 34.02135702300251
        : Number(searchParams.get("lat")) || 34.02135702300251,
      longitude: acctId
        ? -118.38097734936096
        : Number(searchParams.get("lng")) || -118.38097734936096,
      zoom: acctId ? 5 : 7
    };
  });
  const [propertiesArr, setPropertiesArr] = useState<any>([]);
  const [centerPin] = useState<any>({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [initialCoords.longitude, initialCoords.latitude]
    },
    properties: {
      sortOrder: 1,
      icon: "center-marker",
      price: formatAbbreviatedCurrency(
        Number(searchParams.get("price")),
        "USD"
      ),
      sortKey: Number(searchParams.get("price")) * 100000000
    }
  });
  const [popupInfo, setPopupInfo] = useState<{
    longitude: number;
    latitude: number;
    acctName: string;
    address: string;
    currentSalesPrice?: number | string | null;
    currentSaleRecordingDate?: string | number | null;
  } | null>(null);

  React.useEffect(() => {
    if (!flipperVaultPRE?.items?.length) return;
    if (acctId) {
      setPropertiesArr(() => {
        return {
          type: "FeatureCollection",
          features:
            flipperVaultPRE?.items
              ?.filter((item: any) => item?.acctId === acctId)
              .map((item: any) => ({
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [item?.situsLongitude, item?.situsLatitude, 0]
                },
                properties: {
                  ...item,
                  key: JSON.stringify({
                    lat: item?.situsLatitude,
                    lng: item?.situsLongitude
                  }),
                  acctName: item?.acctName,
                  address: `${item?.situsFullAddress}, ${item?.situsCity}, ${item?.situsState} ${item?.situsZIP5}`,
                  id: item?.itemId,
                  lat: item?.situsLatitude,
                  lng: item?.situsLongitude,
                  sortKey: +item?.currentSalesPrice,
                  icon:
                    item?.acctId === visibleCardId
                      ? "green-marker"
                      : "black-marker",
                  price: `${formatAbbreviatedCurrency(
                    item?.currentSalesPrice,
                    "USD"
                  )}`
                }
              })) || []
        };
      });
    } else {
      setPropertiesArr(() => {
        return {
          type: "FeatureCollection",
          features:
            flipperVaultPRE?.items?.map((item: any) => ({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [item?.situsLongitude, item?.situsLatitude, 0]
              },
              properties: {
                ...item,
                key: JSON.stringify({
                  lat: item?.situsLatitude,
                  lng: item?.situsLongitude
                }),
                acctName: item?.acctName,
                address: `${item?.situsFullAddress}, ${item?.situsCity}, ${item?.situsState} ${item?.situsZIP5}`,
                id: item?.itemId,
                lat: item?.situsLatitude,
                lng: item?.situsLongitude,
                sortKey: +item?.currentSalesPrice,
                icon:
                  item?.acctId === visibleCardId
                    ? "green-marker"
                    : "black-marker",
                price: `${formatAbbreviatedCurrency(
                  item?.currentSalesPrice,
                  "USD"
                )}`
              }
            })) || []
        };
      });
    }
  }, [
    flipperVaultPRE?.items,
    flipperVaultPRE?.items?.length,
    expandedId,
    visibleCardId,
    searchParams
  ]);

  const onMapLoad = React.useCallback(async (event: any) => {
    const map = event.target;
    const loadImageAsPromise = (id: string, url: string) => {
      return new Promise<void>((resolve, reject) => {
        if (map.hasImage(id)) return resolve(); // skip if already added

        map.loadImage(url, (error: any, image: any) => {
          if (error) {
            console.error(`Error loading image ${url}:`, error);
            return reject(error);
          }
          map.addImage(id, image);
          resolve();
        });
      });
    };
    try {
      await Promise.all([
        loadImageAsPromise("center-marker", "/images/center-pin.png"),
        loadImageAsPromise("black-marker", "/images/pin-black.png"),
        loadImageAsPromise("green-marker", "/images/pin-green.png")
      ]);

      setStyleLoaded(true); // only after all images are loaded
    } catch (e) {
      console.error("Failed to load one or more images", e);
    }
  }, []);

  const onClick = (event: any) => {
    const feature = event.features[0];
    if (feature) {
      const props = feature.properties || {};
      const newPopupInfo = {
        longitude: feature.geometry.coordinates[0],
        latitude: feature.geometry.coordinates[1],
        acctName: props.acctName,
        address: props.address,
        currentSalesPrice: props.currentSalesPrice,
        currentSaleRecordingDate: props.currentSaleRecordingDate,
      };
      setPopupInfo(null);
      setTimeout(() => {
        setPopupInfo(newPopupInfo);
      }, 0);
    }
  };

  const handleMapMoveEnd = () => {
    // setPopupInfo(null);
  };
  return (
    <div
      className={`${type === "activity" && styles.activityMap} ${
        styles.mapSection
      }`}
    >
      <Map
        initialViewState={{
          latitude: Number(initialCoords.latitude) + -0.8,
          longitude: Number(initialCoords.longitude),
          zoom: Number(initialCoords.zoom),
          bearing: 0,
          pitch: 0
        }}
        mapStyle={"mapbox://styles/mapbox/streets-v12"}
        mapboxAccessToken={TOKEN}
        interactiveLayerIds={[unclusteredPointLayer.id]}
        ref={mapRef}
        onLoad={onMapLoad}
        onStyleData={onMapLoad}
        onClick={onClick}
        onMoveEnd={handleMapMoveEnd}
      >
        <FullscreenControl />
        <GeolocateControl />
        <NavigationControl />
        {styleLoaded && (
          <>
            <Source id="blackPin" type="geojson" data={propertiesArr}>
              <Layer {...unclusteredPointLayer} />
            </Source>
            {!acctId && (
              <Source id="center-pin" type="geojson" data={centerPin}>
                <Layer {...CenteredPinLayer} />
              </Source>
            )}
            {popupInfo && (
              <Popup
                longitude={popupInfo.longitude}
                latitude={popupInfo.latitude}
                anchor="bottom"
                closeOnClick={true}
                onClose={() => setPopupInfo(null)}
                className={styles.mapPopup}
              >
                <div className={styles.popupContent}>
                  <div
                    className={styles.accName}
                    onClick={() => {
                      if (popupInfo.acctName) {
                        navigator.clipboard
                          .writeText(popupInfo.acctName)
                          .then(() => {})
                          .catch((err) => {
                            console.error("Failed to copy: ", err);
                          });
                      }
                    }}
                  >
                    <img src="/images/user.svg" alt="" />
                    {popupInfo.acctName}
                  </div>
                  <div className={styles.divider}></div>
                  <div className={styles.address}>
                    <img src="/images/location.svg" alt="" />
                    {popupInfo.address}
                  </div>
                  {popupInfo.currentSaleRecordingDate && (
                    <>
                      <div className={styles.divider}></div>
                      <div className={styles.priceDetails}>
                        {/* Sale Price hidden for now
                        {popupInfo.currentSalesPrice !== undefined &&
                          popupInfo.currentSalesPrice !== null &&
                          popupInfo.currentSalesPrice !== "" &&
                          Number(popupInfo.currentSalesPrice) > 0 && (
                            <div className={styles.priceRow}>
                              <span className={styles.priceLabel}>
                                Sale Price:
                              </span>
                              <span className={styles.priceValue}>
                                {formatCurrency(
                                  Number(popupInfo.currentSalesPrice),
                                  "USD",
                                  "en-US"
                                )}
                              </span>
                            </div>
                          )}
                        */}
                        {(() => {
                          const soldDate = parseRecordingDate(
                            popupInfo.currentSaleRecordingDate
                          );
                          return soldDate ? (
                            <div className={styles.priceRow}>
                              <span className={styles.priceLabel}>
                                Sold Date:
                              </span>
                              <span className={styles.priceValue}>
                                {soldDate}
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </Popup>
            )}
          </>
        )}
      </Map>
    </div>
  );
}
